import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, Send, Trash2, Loader2, Zap, Database } from 'lucide-react';

const API_URL = '/api';

const GenerateEmails = () => {
  const [stats, setStats] = useState({ received: 0, sent: 0 });
  const [messagePreview, setMessagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    loadStats();
    const savedApiKey = localStorage.getItem('apiKey');
    if (savedApiKey) setApiKey(savedApiKey);
    
    const savedGmail = localStorage.getItem('gmailEmail');
    setIsGmailConnected(!!savedGmail);
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const generateWithAI = async () => {
    if (!apiKey) {
      alert('Please configure your AI API key in Settings!');
      return;
    }

    setLoading(true);
    setLoadingText('🔍 Analyzing Database...');

    try {
      const response = await axios.post(`${API_URL}/ai/generate`, {
        apiKey: apiKey
      });

      if (response.data.success) {
        const preview = `Subject: ${response.data.subject}\n\n${response.data.body}`;
        setMessagePreview(preview);

        const stats = `✅ AI Email Generated!\n\n` +
                     `🧠 Database Learning Applied:\n` +
                     `   • Analyzed ${response.data.contextCount} similar emails\n` +
                     `   • From total ${response.data.totalEmails} received emails\n` +
                     `   • Matched your company's writing style\n\n` +
                     `📝 Review and click 'Create Draft' to save!`;

        alert(stats);
      }
    } catch (error) {
      alert(`❌ Generation Failed!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createDraft = async () => {
    if (!isGmailConnected) {
      alert('Please connect Gmail in Settings first!');
      return;
    }

    if (!messagePreview) {
      alert('Please generate an email first!');
      return;
    }

    let subject = '';
    let body = '';

    if (messagePreview.startsWith('Subject:')) {
      const lines = messagePreview.split('\n');
      subject = lines[0].replace('Subject:', '').trim();
      body = lines.slice(2).join('\n').trim();
    } else {
      subject = 'Email Draft';
      body = messagePreview;
    }

    const confirmed = window.confirm(
      `Create draft in Gmail?\n\nSubject: ${subject}\n\nBody: ${body.substring(0, 100)}...\n\n✅ You can add recipients later in Gmail`
    );

    if (!confirmed) return;

    setLoading(true);
    setLoadingText('📧 Creating Draft...');

    try {
      const response = await axios.post(`${API_URL}/gmail/draft`, {
        sessionId: 'react-session',
        subject: subject,
        body: body
      });

      if (response.data.success) {
        alert('✅ Draft created in Gmail!\n\n' +
              '📧 Open Gmail drafts to add recipients\n' +
              '📚 Saved to learning database\n' +
              '💡 Professional HTML formatting applied');

        setMessagePreview('');
        loadStats();
      }
    } catch (error) {
      alert(`❌ Failed to create draft!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const autoGenerate50Emails = async () => {
    if (!isGmailConnected) {
      alert('Please connect Gmail in Settings first!');
      return;
    }

    if (!apiKey) {
      alert('Please configure your AI API key in Settings!');
      return;
    }

    const confirmed = window.confirm(
      '🤖 AUTO GENERATION\n\n' +
      'This will automatically generate 50 emails and save them to:\n' +
      '• Gmail Drafts folder\n' +
      '• Learning Database\n\n' +
      'This process will take 3-5 minutes.\n\n' +
      'Continue?'
    );

    if (!confirmed) return;

    setLoading(true);
    setLoadingText('🤖 Auto-generating 50 emails...');

    try {
      const response = await axios.post(`${API_URL}/auto-generate`, {
        sessionId: 'react-session',
        apiKey: apiKey,
        count: 50
      });

      if (response.data.success) {
        alert(
          `✅ AUTO GENERATION COMPLETE!\n\n` +
          `Generated: ${response.data.generated} emails\n` +
          `Total: ${response.data.total} emails\n\n` +
          `📧 Check Gmail drafts folder\n` +
          `📚 Saved to learning database\n\n` +
          `AI will learn from these emails!`
        );
        loadStats();
      }
    } catch (error) {
      alert(`❌ Auto-generation Failed!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const clearFields = () => {
    setMessagePreview('');
  };

  return (
    <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
          <Loader2 className="h-12 w-12 animate-spin text-white" />
          <p className="mt-4 text-xl font-semibold text-white">{loadingText}</p>
        </div>
      )}

      <Header 
        title="Generate Emails" 
        subtitle="Create professional emails using AI"
        stats={stats}
      />

      <div className="p-8 max-w-5xl mx-auto">
        <div className="space-y-6">
          {/* AI Generator Card */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Sparkles className="h-6 w-6 text-purple-600" />
                AI Email Generator
              </CardTitle>
              <CardDescription className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                AI learns from {stats.received.toLocaleString()} emails in your database
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={generateWithAI}
                className="w-full h-14 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg"
                disabled={!apiKey}
              >
                <Sparkles className="mr-2 h-6 w-6" />
                Generate Single Email with AI
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-purple-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-purple-600 font-semibold">Bulk Generation</span>
                </div>
              </div>

              <Button
                onClick={autoGenerate50Emails}
                variant="outline"
                className="w-full h-14 border-2 border-purple-600 text-purple-600 hover:bg-purple-50 text-lg font-semibold"
                disabled={!isGmailConnected || !apiKey}
              >
                <Zap className="mr-2 h-6 w-6" />
                Auto-Generate 50 Emails
              </Button>
              <p className="text-xs text-center text-gray-500">
                Automatically creates 50 professional emails and saves to drafts + database
              </p>

              {(!apiKey || !isGmailConnected) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  <p className="font-semibold mb-1">⚠️ Setup Required</p>
                  <p className="text-xs">
                    {!isGmailConnected && '• Connect Gmail in Settings\n'}
                    {!apiKey && '• Configure API key in Settings'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Generated Email Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={messagePreview}
                readOnly
                placeholder="Generated email will appear here..."
                rows="15"
                className="font-mono text-sm resize-none"
              />
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button
              onClick={createDraft}
              disabled={!messagePreview}
              className="flex-1 h-14 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-lg"
            >
              <Send className="mr-2 h-5 w-5" />
              Create Draft in Gmail
            </Button>
            <Button
              onClick={clearFields}
              variant="destructive"
              className="flex-1 h-14 text-lg"
            >
              <Trash2 className="mr-2 h-5 w-5" />
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateEmails;

